/* eslint-disable */
import type { Color } from "../cash/ui/Color.pb";
import type { Image } from "../cash/ui/Image.pb";
import type { RewardSelection } from "../franklin/ui/reward_selection.pb";
import type { RewardSlotState } from "./common.pb";
import type { Icon } from "./ui_common.pb";

/** Protos that are sent to the app live here */
export interface UiRewardAvatar {
  /** @deprecated */
  url?: string;
  /** @deprecated */
  color?: string;
  image?: Image;
  themed_color?: Color;
}

export interface UiRewardAvatars {
  reward_avatars?: UiRewardAvatar[];
}

export interface UiBoostAttribute {
  type?: UiBoostAttributeType;
}

export enum UiBoostAttributeType {
  EXPIRATION = "EXPIRATION",
  LOCKED = "LOCKED",
  TRENDING = "TRENDING",
  ONLINE_ONLY = "ONLINE_ONLY",
}

/**
 * Static data about a reward e.g. 2% Off at Coffee Shops available between March 1st and
 * March 30th. Once opt-in the selected reward expires in 2 weeks.
 */
export interface UiReward {
  token?: string;
  /**
   * If the category is not null, find the top 2 merchants by transaction count on the client side
   * and use their icon. If no such merchants exist or category is null, fallback to
   * reward_avatars.
   */
  category?: string;
  reward_avatars?: UiRewardAvatars;
  /** Title of the reward e.g. Chipotle */
  title?: string;
  /** Amount or Percentage off e.g. 10% */
  main_text?: string;
  /** URL pointing to the page with reward's program details */
  program_details_url?: string;
  /** URL pointing to the page to use the reward if the reward has an affiliate link component */
  affiliate_link_url?: string;
  /** Override user agent to use in the affiliate link browser. If null, the system user agent is used. */
  user_agent?: string;
  /** Full title to display in detail sheet e.g. 10% Off Each Visit at Chipotle */
  full_title_text?: string;
  /** Absolute global Boost activation timestamp in UTC. */
  activation_date_time_ms?: number;
  /** Absolute global Boost expiration timestamp in UTC. */
  expiration_date_time_ms?: number;
  /** Boost attributes e.g. expiration, locked boost, popularity */
  boost_attributes?: UiBoostAttribute[];
  /**
   * Well formatted discount text which can only in one of these two formats or null:
   * - ${amount} where $ sign is the currency symbol in customer's region
   * - {percentage}%
   */
  discount_text?: string;
  /**
   * If false, the customer should not be able to drag a Boost onto their card
   * to select the boost.
   */
  draggable?: boolean;
  /** The name of the merchant associated with this boost, if any. */
  merchant_name?: string;
  /**
   * list of merchant tokens for which the boost applies
   * boost can be created for a list of parent or child merchants
   * although in practice, it is usually done for a single parent.
   */
  merchant_tokens?: string[];
  /** Token for the boost as represented in offerly. Required field. */
  offerly_token?: string;
}

/**
 * Contains data about the selected reward. In addition to reward_token which points to the static
 * reward data, it can contain customer specific reward information e.g. expires in 2 days.
 */
export interface UiSelectedReward {
  token?: string;
  /** token pointing to the reward */
  reward_token?: string;
}

export interface UiRewardSlot {
  token?: string;
  state?: RewardSlotState;
  /** This is only set when state == OCCUPIED */
  selected_reward?: UiSelectedReward;
}

export interface UiRewardProgramDetails {
  reward_token?: string;
  /** @deprecated */
  detail_rows?: UiRewardProgramDetailsDetailRow[];
  boost_details?: UiRewardProgramDetailsBoostDetail[];
  /** @deprecated */
  footer_text?: string;
  /** Styled footer text in program details half sheet (supports markdown). */
  styled_footer_text?: string;
}

/** @deprecated */
export interface UiRewardProgramDetailsDetailRow {
  label?: string;
  value?: string;
  read_only?: UiRewardProgramDetailsDetailRowReadOnly | undefined;
  open_url?: UiRewardProgramDetailsDetailRowOpenUrl | undefined;
}

export interface UiRewardProgramDetailsDetailRowReadOnly {
}

export interface UiRewardProgramDetailsDetailRowOpenUrl {
  url?: string;
}

export interface UiRewardProgramDetailsBoostDetail {
  icon?: Icon;
  icon_asset?: IconAsset;
  /** A short text to describe the detail, e.g. Max discount of $7.50 */
  detail_text?: string;
}

export interface IconAsset {
  name?: string;
  /** @deprecated */
  url?: string;
  image?: Image;
}

export interface UiRewardSelectionState {
  reward_token?: string;
  locked?: UiRewardSelectionStateLocked | undefined;
  unlocked?: UiRewardSelectionStateUnlocked | undefined;
  unlock_in_progress?: UiRewardSelectionStateUnlockInProgress | undefined;
}

export interface UiRewardSelectionStateLocked {
  /** Unlock rules text */
  styled_progress_text?: string;
}

export interface UiRewardSelectionStateUnlocked {
}

export interface UiRewardSelectionStateUnlockInProgress {
  fallback_text?: string;
  /** Progress until unlock occurs text (supports markdown). */
  styled_progress_text?: string;
  button?: UiRewardSelectionStateUnlockInProgressButton;
}

export interface UiRewardSelectionStateUnlockInProgressButton {
  /** Button display label. At most 64 characters. */
  label?: string;
  /** Deeplink to trigger a client scenario with a boost token passing in the url. */
  url?: string;
}

export interface UiRewardsData {
  /** All possible rewards that are either selectable or selected for this customer */
  rewards?: UiReward[];
  /** Reward program details. */
  program_details?: UiRewardProgramDetails[];
  slots?: UiRewardSlot[];
  /** Tokens pointing to rewards that are selectable */
  selectable_reward_tokens?: string[];
  reward_selection_states?: UiRewardSelectionState[];
  /**
   * Additional information about the reward selections that will be passed back as a result
   * of explicit customer interaction with Boosts (add/replace/remove Boost, opt into a locked
   * Boost challenge, etc.)
   */
  reward_selections?: RewardSelection[];
  /**
   * Only show rewards in the app when this bit is set
   *
   * @deprecated
   */
  show_rewards?: boolean;
  /**
   * This flag is only set if we want to show boost video over other videos in the card drawer where
   * there is no card. This flag can be set for customers with or without card. Clients would have
   * to check for card existence separately.
   *
   * @deprecated
   */
  show_boost_video?: boolean;
  /**
   * Indicates to the clients whether or not they should defer invoking AddOrUpdateReward to
   * allow a customer without a Cash card to interact with the Boost selection screen.
   *
   * @deprecated
   */
  defer_reward_selection?: boolean;
  /** This flag is set to true if the customer has never selected a reward. */
  new_to_boost?: boolean;
  /**
   * Ordered list of tokens similar to `selectable_rewards_tokens` with the inclusion of the
   * selected reward.
   */
  ordered_reward_tokens?: string[];
  /**
   * Set to true if the user has used a boost at least in a recent period of time (
   * defaults to 90 days).
   * Field is used to render conditionally render text for offers tab upsell view.
   * This field is only populated if the user is eligible to see offers tab.
   */
  has_recently_used_boost?: boolean;
}
